import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { savedViewsApi } from '@/lib/api/saved-views.api'
import { userPreferencesApi } from '@/lib/api/user-preferences.api'
import { createLogger } from '@/lib/logger'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  SavedQueryVisibilityEnum,
  SavedView,
  SavedViewRecord,
  SurfaceViewPointer,
  UserPreferenceData,
  ViewConfig,
  ViewPreferences,
} from '@scrambl/shared'

const logger = createLogger('hooks/useModuleViews')

interface UseModuleViewsOptions {
  enabled?: boolean
  moduleType: ModuleTypeEnum
  /**
   * Saved-view surface for an embedded list (`SAVED_VIEW_SURFACES.*` or
   * `collection:<id>`). Omit / null for the module's top-level page.
   */
  surface?: string | null
  canManage?: boolean
}

interface UseModuleViewsResult {
  views: SavedView[]
  userViews: SavedView[]
  orgViews: SavedView[]
  activeViewId?: string
  activeView?: SavedView
  orgDefaultViewId?: string
  isLoading: boolean
  setActiveViewId: (viewId: string) => Promise<void>
  /**
   * `query` is the ScQL from the surface's query bar, when it has one. Passing
   * it stores the query and the table config on the SAME record.
   */
  saveActiveView: (config: ViewConfig, query?: string | null) => Promise<void>
  createView: (
    name: string,
    config: ViewConfig,
    scope?: PreferenceScopeEnum,
    query?: string | null,
  ) => Promise<void>
  setOrgDefaultViewId: (viewId: string) => Promise<void>
  deleteView: (viewId: string) => Promise<void>
}

const EMPTY_PREFS: ViewPreferences = { views: [] }
const EMPTY_RECORDS: SavedViewRecord[] = []

const createViewId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `view_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

const recordScope = (record: SavedViewRecord): PreferenceScopeEnum =>
  record.visibility === SavedQueryVisibilityEnum.ORGANIZATION
    ? PreferenceScopeEnum.ORGANIZATION
    : PreferenceScopeEnum.USER

const toSavedView = (record: SavedViewRecord): SavedView => ({
  id: record.id,
  name: record.name || 'Untitled view',
  scope: recordScope(record),
  config: record.config || {},
  createdAt:
    typeof record.createdAt === 'string'
      ? record.createdAt
      : record.createdAt?.toISOString(),
  updatedAt:
    typeof record.updatedAt === 'string'
      ? record.updatedAt
      : record.updatedAt?.toISOString(),
  query: record.query,
  source: 'record',
})

const normalizeLegacyViews = (
  views: SavedView[] | undefined,
  scope: PreferenceScopeEnum,
): SavedView[] =>
  (views || []).map(view => ({
    id: view.id || createViewId(),
    name: view.name || 'Untitled view',
    scope: view.scope || scope,
    config: view.config || {},
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
    source: 'legacy' as const,
  }))

const toViewPreferences = (
  preferences?: Record<string, unknown>,
): ViewPreferences => {
  if (!preferences) return EMPTY_PREFS
  const rawViews = (preferences as { views?: unknown }).views
  const activeViewId = (preferences as { activeViewId?: unknown }).activeViewId
  const defaultViewId = (preferences as { defaultViewId?: unknown })
    .defaultViewId
  const surfaces = (preferences as { surfaces?: unknown }).surfaces

  return {
    views: Array.isArray(rawViews) ? (rawViews as SavedView[]) : [],
    activeViewId: typeof activeViewId === 'string' ? activeViewId : undefined,
    defaultViewId:
      typeof defaultViewId === 'string' ? defaultViewId : undefined,
    // Every write spreads the parsed blob back, so a key dropped here would
    // be dropped from the row on the next pointer change from ANY surface.
    ...(surfaces && typeof surfaces === 'object' && !Array.isArray(surfaces)
      ? { surfaces: surfaces as Record<string, SurfaceViewPointer> }
      : {}),
  }
}

const toPreferencesRecord = (
  preferences: ViewPreferences,
): Record<string, unknown> => preferences as unknown as Record<string, unknown>

/**
 * Saved views for a module list surface.
 *
 * Views are first-class `saved_queries` rows (`/saved-views`) — the same
 * record type as a saved ScQL query, so one artifact can hold a query, a
 * column layout and a grouping together.
 *
 * Two things deliberately stay in the `UserPreference` views blob because they
 * are per-viewer selection state rather than part of the shared artifact:
 * `activeViewId` (which view *I* am looking at) and `defaultViewId` (which
 * shared view the org opens by default).
 *
 * The blob's `views` array is never deleted. Anything still in it that the
 * backfill migration did not promote is shown alongside the records and gets
 * promoted the first time it is saved, so no view can go missing.
 *
 * `surface` splits an embedded list (Client > Projects) from the module's
 * top-level page: records are filtered server-side by surface, and the two
 * pointers live under `surfaces[surface]` on the SAME preference row (the
 * `moduleType` column is too short for a compound key). Legacy blob views
 * belong to the top-level page only.
 */
export function useModuleViews({
  moduleType,
  surface,
  canManage = false,
  enabled = true,
}: UseModuleViewsOptions): UseModuleViewsResult {
  const queryClient = useQueryClient()
  // Normalised so root is always `null`, never a separate `undefined` key.
  const surfaceKey = surface ?? null
  const recordsQueryKey = ['saved-views', moduleType, surfaceKey] as const

  const { data: viewRecords = EMPTY_RECORDS, isLoading: isLoadingRecords } =
    useQuery({
      enabled,
      queryKey: recordsQueryKey,
      queryFn: async () => {
        try {
          return await savedViewsApi.list(moduleType, surfaceKey)
        } catch (error) {
          // A failing view service must never take the table down with it —
          // fall back to whatever the legacy blob still holds.
          logger.error('[useModuleViews] Failed to load saved views', error)
          return EMPTY_RECORDS
        }
      },
      staleTime: 60_000,
    })

  const { data: userPreferenceData, isLoading: isLoadingUser } = useQuery({
    enabled,
    queryKey: ['view-preferences', moduleType, 'user'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.USER,
      ) as Promise<UserPreferenceData | null>,
  })

  const { data: orgPreferenceData, isLoading: isLoadingOrg } = useQuery({
    enabled,
    queryKey: ['view-preferences', moduleType, 'org'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.ORGANIZATION,
      ) as Promise<UserPreferenceData | null>,
  })

  const userPreferences = toViewPreferences(userPreferenceData?.preferences)
  const orgPreferences = toViewPreferences(orgPreferenceData?.preferences)

  // Where this surface's pointers live: the row's root fields for the
  // top-level page, `surfaces[surfaceKey]` for an embedded list.
  const userPointer: SurfaceViewPointer = surfaceKey
    ? (userPreferences.surfaces?.[surfaceKey] ?? {})
    : userPreferences
  const orgPointer: SurfaceViewPointer = surfaceKey
    ? (orgPreferences.surfaces?.[surfaceKey] ?? {})
    : orgPreferences

  /**
   * Merge a pointer change into a preferences blob. The legacy `views` array
   * is always written back verbatim, and other surfaces' pointers are kept.
   * This is read-modify-write on a row shared by every surface of the module;
   * the preference query is invalidated after each write so only two tabs
   * racing can clobber each other.
   */
  const withPointer = (
    prefs: ViewPreferences,
    rawViews: SavedView[],
    patch: SurfaceViewPointer,
  ): ViewPreferences =>
    surfaceKey
      ? {
          ...prefs,
          views: rawViews,
          surfaces: {
            ...prefs.surfaces,
            [surfaceKey]: { ...prefs.surfaces?.[surfaceKey], ...patch },
          },
        }
      : { ...prefs, views: rawViews, ...patch }

  // Raw legacy payloads, kept verbatim so writing `activeViewId` back never
  // rewrites (or loses) the views array we are migrating away from.
  const legacyUserViewsRaw = userPreferences.views
  const legacyOrgViewsRaw = orgPreferences.views

  // A record that carries `legacyViewId` IS the blob entry with that id.
  const migratedLegacyIds = new Set(
    viewRecords
      .map(record => record.legacyViewId)
      .filter((id): id is string => !!id),
  )

  // Blob views were only ever saved on the top-level page, so an embedded
  // surface never shows (or promotes) them.
  const legacyUserViews = surfaceKey
    ? []
    : normalizeLegacyViews(legacyUserViewsRaw, PreferenceScopeEnum.USER).filter(
        view => !migratedLegacyIds.has(view.id),
      )
  const legacyOrgViews = surfaceKey
    ? []
    : normalizeLegacyViews(
        legacyOrgViewsRaw,
        PreferenceScopeEnum.ORGANIZATION,
      ).filter(view => !migratedLegacyIds.has(view.id))

  const recordViews = viewRecords.map(toSavedView)
  const userViews = [
    ...recordViews.filter(view => view.scope === PreferenceScopeEnum.USER),
    ...legacyUserViews,
  ]
  const orgViews = [
    ...recordViews.filter(
      view => view.scope === PreferenceScopeEnum.ORGANIZATION,
    ),
    ...legacyOrgViews,
  ]

  const views = [...orgViews, ...userViews]

  // The backfill gave every promoted view a fresh record id, so a pointer
  // written before the migration still names the blob entry. Follow it to the
  // record it became rather than dropping the selection.
  const recordIdByLegacyId = new Map(
    viewRecords
      .filter(record => !!record.legacyViewId)
      .map(record => [record.legacyViewId as string, record.id]),
  )
  const resolveViewId = (viewId?: string) =>
    viewId ? (recordIdByLegacyId.get(viewId) ?? viewId) : undefined

  const orgDefaultViewId = resolveViewId(orgPointer.defaultViewId)
  // A view is active only when this viewer picked it or the org made it the
  // default. Falling back to the first view labelled the surface with a view
  // whose config was never applied, so the table read as "Edited" the moment
  // anyone opened the picker.
  const resolvedActiveViewId =
    resolveViewId(userPointer.activeViewId) || orgDefaultViewId
  const activeView = views.find(view => view.id === resolvedActiveViewId)

  const refreshQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: recordsQueryKey }),
      queryClient.invalidateQueries({
        queryKey: ['view-preferences', moduleType],
      }),
    ])
  }

  const saveUserPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.USER,
    )
    await refreshQueries()
  }

  const saveOrgPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.ORGANIZATION,
    )
    await refreshQueries()
  }

  /** Only the pointer changes; the legacy views array rides along untouched. */
  const writeActiveViewId = async (viewId: string | undefined) => {
    await saveUserPreferences(
      withPointer(userPreferences, legacyUserViewsRaw, {
        activeViewId: viewId,
      }),
    )
  }

  const setActiveViewId = async (viewId: string) => {
    try {
      await writeActiveViewId(viewId)
    } catch (error) {
      logger.error('[useModuleViews] Failed to set active view', error)
      toast.error(
        getMutationErrorMessage(error, "Couldn't update the view selection."),
      )
    }
  }

  const createRecordView = async (
    name: string,
    config: ViewConfig,
    scope: PreferenceScopeEnum,
    options: { legacyViewId?: string; query?: string | null } = {},
  ): Promise<SavedViewRecord> =>
    savedViewsApi.create({
      name,
      moduleType,
      surface: surfaceKey,
      config,
      visibility:
        scope === PreferenceScopeEnum.ORGANIZATION
          ? SavedQueryVisibilityEnum.ORGANIZATION
          : SavedQueryVisibilityEnum.PRIVATE,
      ...(options.legacyViewId ? { legacyViewId: options.legacyViewId } : {}),
      ...(options.query !== undefined ? { query: options.query } : {}),
    })

  const createView = async (
    name: string,
    config: ViewConfig,
    scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
    query?: string | null,
  ) => {
    const targetScope =
      scope === PreferenceScopeEnum.ORGANIZATION && canManage
        ? PreferenceScopeEnum.ORGANIZATION
        : PreferenceScopeEnum.USER

    try {
      const created = await createRecordView(
        name.trim() || 'New view',
        config,
        targetScope,
        { query },
      )
      if (targetScope === PreferenceScopeEnum.ORGANIZATION) {
        await refreshQueries()
      } else {
        await writeActiveViewId(created.id)
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to create view', error)
      // Surface the server's reason (e.g. shared views need admin rights, or
      // a query needs the ScQL plan) instead of a generic failure.
      toast.error(getMutationErrorMessage(error, "Couldn't create the view."))
    }
  }

  const saveActiveView = async (config: ViewConfig, query?: string | null) => {
    if (!activeView) {
      await createView('My view', config, PreferenceScopeEnum.USER, query)
      return
    }

    const isShared = activeView.scope === PreferenceScopeEnum.ORGANIZATION
    if (isShared && !canManage) {
      // Same rule as before: a member editing a shared view forks it.
      await createView(
        `My ${activeView.name}`,
        config,
        PreferenceScopeEnum.USER,
        query,
      )
      return
    }

    try {
      if (activeView.source === 'legacy') {
        // Promote on write: anything the backfill missed becomes a record the
        // first time someone saves it, keyed back to the blob entry.
        const created = await createRecordView(
          activeView.name,
          config,
          activeView.scope,
          { legacyViewId: activeView.id, query },
        )
        if (!isShared) {
          await writeActiveViewId(created.id)
        } else {
          await refreshQueries()
        }
        return
      }

      await savedViewsApi.update(activeView.id, {
        config,
        ...(query !== undefined ? { query } : {}),
      })
      await refreshQueries()
    } catch (error) {
      logger.error('[useModuleViews] Failed to save view', error)
      toast.error(getMutationErrorMessage(error, "Couldn't save the view."))
    }
  }

  const setOrgDefaultViewId = async (viewId: string) => {
    if (!canManage) return
    if (!orgViews.some(view => view.id === viewId)) return
    try {
      await saveOrgPreferences(
        withPointer(orgPreferences, legacyOrgViewsRaw, {
          defaultViewId: viewId,
        }),
      )
    } catch (error) {
      logger.error('[useModuleViews] Failed to set org default view', error)
      toast.error(
        getMutationErrorMessage(error, "Couldn't update the default view."),
      )
    }
  }

  const deleteView = async (viewId: string) => {
    const view = views.find(item => item.id === viewId)
    if (!view) return

    const isShared = view.scope === PreferenceScopeEnum.ORGANIZATION
    if (isShared && !canManage) {
      toast.error('You do not have permission to delete shared views')
      return
    }

    const storedActiveViewId = userPointer.activeViewId
    const remaining = views.filter(item => item.id !== viewId)
    const nextActiveViewId =
      storedActiveViewId === viewId
        ? remaining.find(item => item.id === orgDefaultViewId)?.id ||
          remaining[0]?.id
        : storedActiveViewId

    try {
      if (view.source === 'legacy') {
        // Legacy views only exist at root, so these writes target root fields.
        if (isShared) {
          await saveOrgPreferences(
            withPointer(
              orgPreferences,
              legacyOrgViewsRaw.filter(item => item.id !== viewId),
              {
                defaultViewId:
                  orgPointer.defaultViewId === viewId
                    ? undefined
                    : orgPointer.defaultViewId,
              },
            ),
          )
        } else {
          await saveUserPreferences(
            withPointer(
              userPreferences,
              legacyUserViewsRaw.filter(item => item.id !== viewId),
              { activeViewId: nextActiveViewId },
            ),
          )
          return
        }
      } else {
        await savedViewsApi.remove(viewId)
        if (isShared && orgPointer.defaultViewId === viewId) {
          await saveOrgPreferences(
            withPointer(orgPreferences, legacyOrgViewsRaw, {
              defaultViewId: undefined,
            }),
          )
        }
      }

      if (storedActiveViewId === viewId) {
        await writeActiveViewId(nextActiveViewId)
      } else {
        await refreshQueries()
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to delete view', error)
      toast.error(
        isShared
          ? "Couldn't delete the shared view."
          : "Couldn't delete the view.",
      )
    }
  }

  return {
    views,
    userViews,
    orgViews,
    activeViewId: resolvedActiveViewId,
    activeView,
    orgDefaultViewId,
    isLoading: isLoadingRecords || isLoadingUser || isLoadingOrg,
    setActiveViewId,
    saveActiveView,
    createView,
    setOrgDefaultViewId,
    deleteView,
  }
}
